import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../../../../context/AuthContext';
import {
  getSafetyCategories,
  getRiskCauses,
  getSafetyBasicInfo,
  getSafetyProcessMasters,
  getSafetyMasterMappings,
  saveAssessEvaluation,
  uploadAssessPhoto,
  deleteAssessPhoto
} from '../../../../api/safetyAssessApi';
import OrgTreeSelect from '../../../../components/OrgSelect/OrgTreeSelect';
import ModalSchedule from '../../../../pages/schedule/ModalSchedule';
import '../../../../pages/schedule/ModalSchedule.css';
import '../../../../pages/schedule/Schedule.css';
import CommonDatePicker from '../../../../components/DatePicker/CommonDatePicker';
import { STRENGTH_OPTIONS, FREQUENCY_OPTIONS } from '../../../../utils/riskUtils';

function RiskFormModal({ id, assessmentId, assessmentName, matrixCheckId, initialData, onClose, onSaved, readOnly = false }) {
  const { token } = useContext(AuthContext);

  // 자동 포커스 이동을 위한 DOM Refs
  const categoryIdRef = useRef(null);
  const causeIdRef = useRef(null);
  const currentMeasureRef = useRef(null);
  const actionRef = useRef(null);
  const freqBeforeRef = useRef(null);
  const sevBeforeRef = useRef(null);
  const freqAfterRef = useRef(null);
  const sevAfterRef = useRef(null);
  const assigneesRef = useRef(null);
  const dueDateRef = useRef(null);
  const doneDateRef = useRef(null);

  const focusRef = (ref) => {
    if (ref && ref.current) {
      setActiveTab('FORM');
      setTimeout(() => {
        if (ref.current.scrollIntoView) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (ref.current.focus) {
          ref.current.focus();
        }
      }, 50);
    }
  };

  const [categories, setCategories] = useState([]);
  const [causes, setCauses] = useState([]);
  const [useProcess, setUseProcess] = useState(true);
  const [evalMethod, setEvalMethod] = useState('3X3');
  const [masterProcesses, setMasterProcesses] = useState([]);
  const [masterMappings, setMasterMappings] = useState([]);
  const [selectedProcessIds, setSelectedProcessIds] = useState([]);

  // 입력 폼 필드
  const [categoryId, setCategoryId] = useState('');
  const [causeId, setCauseId] = useState('');
  const [currentMeasure, setCurrentMeasure] = useState('');
  const [action, setAction] = useState('');

  // 신규 등록(직접 등록) 여부 플래그
  const isNew = !id;

  // 탭 제어 상태 (폼 영역과 사진 영역 분리)
  const [activeTab, setActiveTab] = useState('FORM'); // 'FORM' | 'PHOTO'

  // 개선 전/후 위험도
  const [freqBefore, setFreqBefore] = useState('');
  const [sevBefore, setSevBefore] = useState('');
  const [freqAfter, setFreqAfter] = useState('');
  const [sevAfter, setSevAfter] = useState('');

  // 담당자 정보
  const [assignees, setAssignees] = useState([]);
  const [deptName, setDeptName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [doneDate, setDoneDate] = useState('');
  const [actionStatus, setActionStatus] = useState('pending');

  // 사진 관리
  const [photos, setPhotos] = useState([]);
  const [pendingBeforeFile, setPendingBeforeFile] = useState(null);
  const [pendingAfterFile, setPendingAfterFile] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // 조직도 모달
  const [orgPopupOpen, setOrgPopupOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchUsers, setSearchUsers] = useState([]);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [isChargeExpanded, setIsChargeExpanded] = useState(false);

  const addAssignee = (u) => {
    const empNo = String(u.userid || u.emp_no || u.id || '');
    setAssignees(prev => {
      if (prev.some(c => c.emp_no === empNo)) return prev;
      return [...prev, {
        emp_no: empNo,
        name: u.name || '',
        position: u.position || '',
        department: u.dept_name || u.department || '',
        checked: false,
      }];
    });
  };

  const toggleCheck = (empNo) => {
    setAssignees(prev =>
      prev.map(c => c.emp_no === empNo ? { ...c, checked: !c.checked } : c)
    );
  };

  const deleteSelected = () => setAssignees(prev => prev.filter(c => !c.checked));
  const deleteAll = () => setAssignees([]);

  const handleSearch = (term) => {
    const keyword = (term || '').trim();
    if (!keyword) { alert('검색대상이 입력되지 않았습니다.'); return; }
    axios.get('/api/org/search', {
      params: { keyword },
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const users = (res.data.users || []);
      if (users.length === 0) { alert('검색 결과가 존재하지 않습니다.'); return; }
      if (users.length === 1) { addAssignee(users[0]); setSearchTerm(''); return; }
      setSearchUsers(users.map(u => ({ ...u, checked: false })));
      setShowSearchPopup(true);
    }).catch(err => console.error('검색 실패:', err));
  };

  const toggleSearchSelect = (item) => {
    setSearchUsers(prev =>
      prev.map(u => u.userid === item.userid ? { ...u, checked: !u.checked } : u)
    );
  };

  const handleSearchConfirm = () => {
    searchUsers.filter(u => u.checked).forEach(u => addAssignee(u));
    setSearchUsers([]);
    setShowSearchPopup(false);
    setSearchTerm('');
  };

  useEffect(() => {
    fetchMetadata();
    if (initialData) {
      if (initialData.processId) {
        setSelectedProcessIds(String(initialData.processId).split(',').map(val => val === 'NONE' ? 'NONE' : parseInt(val, 10)));
      }
      setCauseId(initialData.causeId || '');
      if (initialData.evaluation) {
        const ev = initialData.evaluation;
        setCurrentMeasure(ev.currentMeasure || initialData.memo || '');
        setAction(ev.action || '');
        setFreqBefore(ev.freqBefore || '');
        setSevBefore(ev.sevBefore || '');
        setFreqAfter(ev.freqAfter || '');
        setSevAfter(ev.sevAfter || '');
        setAssignees(ev.assignees || []);
        setDeptName(ev.deptName || '');
        setDueDate(ev.dueDate ? new Date(ev.dueDate) : null);
        setDoneDate(ev.doneDate ? new Date(ev.doneDate) : null);
        setActionStatus(ev.actionStatus || 'pending');
        setPhotos(ev.photos || []);
      } else {
        setCurrentMeasure(initialData.memo || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const fetchMetadata = async () => {
    try {
      const [catData, causeData, basicInfo, masterProcData, masterMapData] = await Promise.all([
        getSafetyCategories(token),
        getRiskCauses('', token),
        getSafetyBasicInfo(token),
        getSafetyProcessMasters(token),
        getSafetyMasterMappings(token)
      ]);
      setCategories(catData || []);
      setCauses(causeData || []);
      if (basicInfo && basicInfo.useProcess !== undefined) {
        setUseProcess(basicInfo.useProcess);
      }
      if (basicInfo && basicInfo.evalMethod) {
        setEvalMethod(basicInfo.evalMethod);
      }
      setMasterProcesses(masterProcData || []);
      setMasterMappings(masterMapData || []);

      if (initialData && initialData.causeId) {
        const selectedCause = causeData.find(c => c.id === initialData.causeId);
        if (selectedCause) setCategoryId(selectedCause.categoryId);
      }
    } catch (error) {
      console.error('메타데이터 로드 실패:', error);
    }
  };

  const getRiskScore = (f, s) => {
    if (!f || !s) return null;
    return parseInt(f, 10) * parseInt(s, 10);
  };

  const getBadgeStyleAndLabel = (score) => {
    if (!score) return { color: '#64748b', text: '-', label: '미평가' };
    const s = parseInt(score, 10);
    if (evalMethod === '3X3') {
      if (s === 1 || s === 2) return { color: '#10b981', text: `${s}점`, label: '하' };
      if (s === 3 || s === 4) return { color: '#f59e0b', text: `${s}점`, label: '중' };
      return { color: '#ef4444', text: `${s}점`, label: '상' };
    } else {
      if (s <= 4) return { color: '#3b82f6', text: `${s}점`, label: '낮음' };
      if (s <= 8) return { color: '#10b981', text: `${s}점`, label: '보통' };
      if (s <= 16) return { color: '#f59e0b', text: `${s}점`, label: '높음' };
      return { color: '#ef4444', text: `${s}점`, label: '치명적' };
    }
  };

  const handleOrgConfirm = (selectedTargets) => {
    setOrgPopupOpen(false);
    if (!selectedTargets || selectedTargets.length === 0) return;
    const selectedWorkers = selectedTargets.map(t => ({
      emp_no: t.target_id, name: t.target_name, position: t.position || '사원', department: t.dept_name || '', checked: false
    }));
    setAssignees(selectedWorkers);
    if (selectedTargets[0].dept_name) {
      setDeptName(selectedTargets[0].dept_name);
    }
  };

  const handlePhotoUploadLocal = async (type, e) => {
    const fileObj = e.target && e.target.files ? e.target.files : (e.dataTransfer ? e.dataTransfer.files : null);
    const file = fileObj && fileObj.length > 0 ? fileObj[0] : null;
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);

    setPhotos(prev => {
      const filtered = prev.filter(p => p.type !== type);
      return [...filtered, { id: `temp_${type}`, type, url: previewUrl, isLocal: true }];
    });

    if (type === 'BEFORE') setPendingBeforeFile(file);
    else setPendingAfterFile(file);

    if (id) {
      try {
        await uploadAssessPhoto(id, type, file, token);
        if (onSaved) onSaved();
      } catch (err) {
        console.error('사진 업로드 실패', err);
      }
    }
  };

  const handlePhotoDeleteLocal = async (photo, type) => {
    if (!window.confirm('등록된 사진을 삭제하시겠습니까?')) return;
    if (photo.isLocal || String(photo.id).startsWith('temp_')) {
      setPhotos(prev => prev.filter(p => p.type !== type));
      if (type === 'BEFORE') setPendingBeforeFile(null);
      else setPendingAfterFile(null);
      return;
    }
    try {
      await deleteAssessPhoto(photo.id, token);
      setPhotos(prev => prev.filter(p => p.type !== type));
      if (type === 'BEFORE') setPendingBeforeFile(null);
      else setPendingAfterFile(null);
      if (onSaved) onSaved();
    } catch (err) {
      console.error('사진 삭제 실패:', err);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    let finalDoneDate = doneDate;
    // 기본 필수 항목 검증
    if (!categoryId) {
      alert('유해위험 대분류를 선택해주세요.');
      focusRef(categoryIdRef);
      return;
    }
    if (!causeId) {
      alert('유해위험 요인 원인을 선택해주세요.');
      focusRef(causeIdRef);
      return;
    }
    if (!currentMeasure || !currentMeasure.trim()) {
      alert('유해위험요인 파악 내용을 입력해주세요.');
      focusRef(currentMeasureRef);
      return;
    }
    if (useProcess && isNew && selectedProcessIds.length === 0) {
      alert('대상 공정을 최소 하나 이상 선택해 주세요.');
      return;
    }
    // 개선 전 위험도 필수 검증
    if (!freqBefore) {
      alert('개선 전 위험도의 빈도를 지정해야 합니다.');
      focusRef(freqBeforeRef);
      return;
    }
    if (!sevBefore) {
      alert('개선 전 위험도의 강도를 지정해야 합니다.');
      focusRef(sevBeforeRef);
      return;
    }
    // 담당자 필수 검증
    if (!assignees || assignees.length === 0) {
      alert('개선 조치 담당자를 최소 1명 이상 지정해 주세요.');
      focusRef(assigneesRef);
      return;
    }
    // 진행중/완료 상태일 때 조치예정일 필수 검증
    if ((actionStatus === 'in_progress' || actionStatus === 'completed') && !dueDate) {
      alert(`조치상태가 [${actionStatus === 'in_progress' ? '진행중' : '조치 완료'}]일 경우, 조치예정일을 반드시 지정해야 합니다.`);
      focusRef(dueDateRef);
      return;
    }

    // 조건부 필수 검증 (위험도 + 조치상태 기반)
    const _scoreBefore = getRiskScore(freqBefore, sevBefore);
    const _isRiskHigh = _scoreBefore !== null && _scoreBefore >= 3;
    const _hasAction = action && action.trim().length > 0;
    const _hasDueDate = !!dueDate;
    const _hasAfterRisk = !!freqAfter || !!sevAfter;
    const _isPending = actionStatus === 'pending';

    // 조치 상태가 [조치 완료]일 경우 전수 필수 항목 유효성 검사 및 자동 포커스 이동
    if (actionStatus === 'completed') {
      if (!currentMeasure || !currentMeasure.trim()) {
        alert('조치상태가 [조치 완료]일 경우, 유해위험요인 파악 내용을 반드시 입력해야 합니다.');
        focusRef(currentMeasureRef);
        return;
      }
      if (!action || !action.trim()) {
        alert('조치상태가 [조치 완료]일 경우, 개선(감소)대책 수립 내용을 반드시 입력해야 합니다.');
        focusRef(actionRef);
        return;
      }
      if (!freqAfter) {
        alert('조치상태가 [조치 완료]일 경우, 개선 후 위험도(빈도)를 지정해야 합니다.');
        focusRef(freqAfterRef);
        return;
      }
      if (!sevAfter) {
        alert('조치상태가 [조치 완료]일 경우, 개선 후 위험도(강도)를 지정해야 합니다.');
        focusRef(sevAfterRef);
        return;
      }
      if (!finalDoneDate) {
        if (window.confirm('조치완료일이 입력되지 않았습니다. 오늘 날짜로 지정하여 완료 처리하시겠습니까?')) {
          finalDoneDate = new Date().toISOString().split('T')[0];
          setDoneDate(finalDoneDate);
        } else {
          focusRef(doneDateRef);
          return;
        }
      }
    }

    // 진행중/완료 상태에서 위험도 3점 이상이면 개선대책 필수
    if (!_isPending && _isRiskHigh && !_hasAction) {
      alert('진행상태가 [진행중] 이상이고 개선 전 위험도가 보통(3점) 이상이므로, 개선(감소)대책을 반드시 입력해야 합니다.');
      focusRef(actionRef);
      return;
    }
    if ((_hasDueDate || _hasAfterRisk) && !_hasAction) {
      alert('조치예정일 또는 개선 후 위험도가 입력된 경우, 개선(감소)대책을 반드시 입력해야 합니다.');
      focusRef(actionRef);
      return;
    }
    if (_hasAction && (!freqAfter || !sevAfter)) {
      alert('개선(감소)대책이 수립된 경우, 개선 후 위험도(빈도/강도)를 반드시 지정해야 합니다.');
      focusRef(!freqAfter ? freqAfterRef : sevAfterRef);
      return;
    }
    if (_hasAction && !_hasDueDate) {
      alert('개선(감소)대책이 수립된 경우, 조치예정일을 반드시 지정해야 합니다.');
      focusRef(dueDateRef);
      return;
    }
    try {
      const scoreBefore = getRiskScore(freqBefore, sevBefore);
      const scoreAfter = getRiskScore(freqAfter, sevAfter);

      const hasChangedMapping = (selectedProcessIds.join(',') !== String(initialData?.processId || '')) || (parseInt(causeId, 10) !== initialData?.causeId);

      const buildPayload = () => ({
        id: isNew ? undefined : id,
        assessmentId: assessmentId,
        matrixCheckId: (isNew || hasChangedMapping) ? null : (matrixCheckId || null),
        processId: selectedProcessIds.includes('NONE') ? 'NONE' : selectedProcessIds.join(','),
        causeId: causeId ? parseInt(causeId, 10) : null,
        currentMeasure,
        action,
        freqBefore: freqBefore ? parseInt(freqBefore, 10) : null,
        sevBefore: sevBefore ? parseInt(sevBefore, 10) : null,
        riskBefore: scoreBefore,
        freqAfter: freqAfter ? parseInt(freqAfter, 10) : null,
        sevAfter: sevAfter ? parseInt(sevAfter, 10) : null,
        riskAfter: scoreAfter,
        dueDate: dueDate ? (dueDate instanceof Date ? dueDate.toISOString().split('T')[0] : dueDate.split('T')[0]) : null,
        doneDate: finalDoneDate ? (finalDoneDate instanceof Date ? finalDoneDate.toISOString().split('T')[0] : finalDoneDate.split('T')[0]) : null,
        assignees: assignees,
        deptName,
        actionStatus
      });

      let savedIds = [];
      const res = await saveAssessEvaluation(buildPayload(), token);
      if (res && res.id) savedIds.push(res.id);
      else if (id) savedIds.push(id);

      if (savedIds.length > 0) {
        const photoPromises = [];
        for (const savedId of savedIds) {
          if (pendingBeforeFile) {
            photoPromises.push(uploadAssessPhoto(savedId, 'BEFORE', pendingBeforeFile, token));
          }
          if (pendingAfterFile) {
            photoPromises.push(uploadAssessPhoto(savedId, 'AFTER', pendingAfterFile, token));
          }
        }
        if (photoPromises.length > 0) {
          await Promise.all(photoPromises);
        }
      }

      alert('평가 정보가 안전하게 저장되었습니다.');
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error(error);
      alert('저장 실패');
    }
  };

  const beforePhoto = photos.find(p => p.type === 'BEFORE');
  const afterPhoto = photos.find(p => p.type === 'AFTER');

  const mappedMasterProcessIds = masterMappings
    .filter(m => m.causeId === parseInt(causeId, 10))
    .map(m => m.processId);
  const availableProcesses = masterProcesses.filter(proc => mappedMasterProcessIds.includes(proc.id));

  const handleNoneCheckboxChange = (e) => {
    setSelectedProcessIds(e.target.checked ? ['NONE'] : []);
  };

  const handleProcessCheckboxChange = (procId, checked) => {
    setSelectedProcessIds(prev => {
      const withoutNone = prev.filter(id => id !== 'NONE');
      return checked ? [...withoutNone, procId] : withoutNone.filter(id => id !== procId);
    });
  };

  const isNoneChecked = selectedProcessIds.includes('NONE');

  // 조건부 필수 판정 (동적 * 표시용)
  const _riskScoreBefore = getRiskScore(freqBefore, sevBefore);
  const _riskHigh = _riskScoreBefore !== null && _riskScoreBefore >= 3;
  const _actionFilled = action && action.trim().length > 0;
  const _dueDateFilled = !!dueDate;
  const _afterRiskFilled = !!freqAfter || !!sevAfter;
  const _isPending = actionStatus === 'pending';
  // 대기: 위험도 높아도 개선대책 강제 안 함. 진행중/완료: 위험도 3점 이상이면 강제
  const isActionRequired = (!_isPending && _riskHigh) || _dueDateFilled || _afterRiskFilled;
  const isAfterRiskRequired = _actionFilled;
  const isDueDateRequired = actionStatus === 'in_progress' || actionStatus === 'completed' || _actionFilled;
  const isDoneDateRequired = actionStatus === 'completed';

  return ReactDOM.createPortal(
    <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9998, overflow: 'hidden' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1200px', width: '95%' }}>
        <div className="modal-content" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 40px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
          <form onSubmit={handleSave}>
            <div className="modal-header d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: '#fff', padding: '16px 24px' }}>
              <h5 className="modal-title fw-bold m-0 fs-4"><i className="fas fa-shield-alt me-2"></i>위험성 평가 및 조치등록</h5>
              <button type="button" className="btn-close btn-close-white" onClick={onClose} style={{ boxShadow: 'none' }}></button>
            </div>

            {/* 상단 탭 네비게이션 */}
            <div className="bg-light border-bottom px-4 pt-3">
              <ul className="nav nav-tabs border-0" style={{ gap: '10px' }}>
                <li className="nav-item">
                  <button type="button" className={`nav-link fw-bold ${activeTab === 'FORM' ? 'active' : ''}`} style={{ border: 'none', borderBottom: activeTab === 'FORM' ? '3px solid #3b82f6' : '3px solid transparent', color: activeTab === 'FORM' ? '#1e3a8a' : '#64748b', backgroundColor: 'transparent', padding: '10px 20px', fontSize: '1rem' }} onClick={() => setActiveTab('FORM')}>
                    <i className="fas fa-file-alt me-2"></i>기본 평가 및 조치 내역
                  </button>
                </li>
                <li className="nav-item">
                  <button type="button" className={`nav-link fw-bold ${activeTab === 'PHOTO' ? 'active' : ''}`} style={{ border: 'none', borderBottom: activeTab === 'PHOTO' ? '3px solid #3b82f6' : '3px solid transparent', color: activeTab === 'PHOTO' ? '#1e3a8a' : '#64748b', backgroundColor: 'transparent', padding: '10px 20px', fontSize: '1rem' }} onClick={() => setActiveTab('PHOTO')}>
                    <i className="fas fa-camera me-2"></i>현장 증빙 사진
                  </button>
                </li>
              </ul>
            </div>

            <style>{`
              .react-datepicker-wrapper {
                width: 150px !important;
              }
              .react-datepicker__input-container {
                width: 150px !important;
              }
              .react-datepicker__input-container input {
                width: 150px !important;
                border-color: #cbd5e1 !important;
                height: 34px !important;
                border-radius: 6px !important;
                font-size: 0.825rem !important;
                padding: 6px 10px !important;
              }
              .form-select, .form-control {
                font-size: 0.825rem !important;
              }
              .form-control::placeholder {
                color: #94a3b8 !important;
                font-size: 0.825rem !important;
                opacity: 0.8;
              }
            `}</style>

            <div className="modal-body" style={{ padding: '20px 30px', height: '65vh', overflowY: 'auto', backgroundColor: '#f8fafc' }}>
              {activeTab === 'FORM' && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-list-alt me-1 text-primary"></i> 위험성평가명
                      </label>
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ backgroundColor: '#f1f5f9', fontSize: '0.85rem' }}>
                        {assessmentName || '위험성평가 정보 없음'}
                      </div>
                    </div>
                  </div>

                  {useProcess ? (
                    <>
                      {/* 공정 사용 시: 좌측 대분류 + 원인 1개 통합 카드 */}
                      <div className="col-md-6">
                        <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column gap-3" style={{ borderColor: '#e2e8f0', height: '100%' }}>
                          <div>
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-folder me-1 text-primary"></i> 유해위험 대분류<span className="text-danger ms-1">*</span>
                            </label>
                            <select ref={categoryIdRef} className="form-select" value={categoryId} onChange={e => { setCategoryId(e.target.value); setCauseId(''); }} disabled={readOnly} style={{ fontSize: '0.825rem', height: '36px', borderColor: '#cbd5e1' }} required>
                              <option value="">대분류 선택</option>
                              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-exclamation-triangle me-1 text-primary"></i> 유해위험 원인<span className="text-danger ms-1">*</span>
                            </label>
                            <select ref={causeIdRef} className="form-select" value={causeId} onChange={e => setCauseId(e.target.value)} disabled={readOnly || !categoryId} style={{ fontSize: '0.825rem', height: '36px', borderColor: '#cbd5e1' }} required>
                              <option value="">세부 원인 선택</option>
                              {causes.filter(c => c.categoryId === parseInt(categoryId, 10)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                      {/* 공정 사용 시: 우측 공정 다중 선택 카드 (좌측과 동일 높이 매칭) */}
                      <div className="col-md-6">
                        <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0', height: '100%', minHeight: '172px' }}>
                          <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-tasks me-1 text-primary"></i> 대상 공정 선택<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="flex-grow-1 border rounded-2 p-2" style={{ backgroundColor: '#f8fafc', overflowY: 'auto', maxHeight: '110px' }}>
                            <label className="d-block mb-2 fw-semibold" style={{ fontSize: '0.825rem', cursor: 'pointer', color: '#475569' }}>
                              <input type="checkbox" checked={isNoneChecked} onChange={handleNoneCheckboxChange} disabled={readOnly} className="me-2" />
                              사업장 공통
                            </label>
                            {availableProcesses.map(proc => (
                              <label key={proc.id} className="d-block mb-1" style={{ fontSize: '0.825rem', cursor: isNoneChecked ? 'not-allowed' : 'pointer', color: isNoneChecked ? '#94a3b8' : '#334155' }}>
                                <input
                                  type="checkbox"
                                  checked={!isNoneChecked && selectedProcessIds.includes(proc.id)}
                                  disabled={isNoneChecked}
                                  onChange={(e) => handleProcessCheckboxChange(proc.id, e.target.checked)}
                                  className="me-2"
                                />
                                {proc.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* 기존 공정 미사용일 때의 분류/원인 개별 카드 2개 UI */}
                      <div className="col-md-6">
                        <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0', height: '100%' }}>
                          <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-folder me-1 text-primary"></i> 유해위험 대분류<span className="text-danger ms-1">*</span>
                          </label>
                          <select className="form-select" value={categoryId} onChange={e => { setCategoryId(e.target.value); setCauseId(''); }} disabled={readOnly || !isNew} style={{ fontSize: '0.825rem', height: '36px', borderColor: '#cbd5e1' }} required>
                            <option value="">대분류 선택</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0', height: '100%' }}>
                          <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-exclamation-triangle me-1 text-primary"></i> 유해위험 원인<span className="text-danger ms-1">*</span>
                          </label>
                          <select className="form-select" value={causeId} onChange={e => setCauseId(e.target.value)} disabled={readOnly || !isNew} style={{ fontSize: '0.825rem', height: '36px', borderColor: '#cbd5e1' }} required>
                            <option value="">세부 원인 선택</option>
                            {causes.filter(c => c.categoryId === parseInt(categoryId, 10)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {/* 2. 유해위험 요인 및 대책 */}
                  <div className="col-12">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-search me-1 text-danger"></i> 유해위험요인 파악<span className="text-danger ms-1">*</span>
                      </label>
                      <textarea ref={currentMeasureRef} className="form-control" rows="4" value={currentMeasure} onChange={e => setCurrentMeasure(e.target.value)} readOnly={readOnly} placeholder="현장 조감도 및 순회점검 상의 위험요인을 구체적으로 적어주세요." style={{ border: 'none', backgroundColor: '#f1f5f9', resize: 'none', fontSize: '0.825rem' }} required />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-check-square me-1 text-success"></i> 개선(감소)대책 수립{isActionRequired && <span className="text-danger ms-1">*</span>}
                      </label>
                      <textarea ref={actionRef} className="form-control" rows="4" value={action} onChange={e => setAction(e.target.value)} readOnly={readOnly} placeholder="위험을 감소시키기 위한 현실적인 대책을 기재해 주세요." style={{ border: 'none', backgroundColor: '#f1f5f9', resize: 'none', fontSize: '0.825rem' }} />
                    </div>
                  </div>

                  {/* 3. 위험성 평가 (개선 전/후) */}
                  <div className="col-md-6">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <h6 className="fw-bold text-secondary mb-3" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-shield-alt me-1 text-danger"></i> 개선 전 위험도<span className="text-danger ms-1">*</span>
                      </h6>
                      <div className="p-3 rounded-2" style={{ background: '#fff1f2', border: '1px solid #fda4af' }}>
                        <div className="row g-2">
                          <div className="col-6">
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.825rem' }}>빈도</label>
                            <select ref={freqBeforeRef} className="form-select" value={freqBefore} onChange={e => setFreqBefore(e.target.value)} disabled={readOnly} style={{ fontSize: '0.825rem' }} required>
                              {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                          <div className="col-6">
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.825rem' }}>강도</label>
                            <select ref={sevBeforeRef} className="form-select" value={sevBefore} onChange={e => setSevBefore(e.target.value)} disabled={readOnly} style={{ fontSize: '0.825rem' }} required>
                              {STRENGTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                          <div className="col-12 mt-2">
                            <span className="fw-bold text-secondary" style={{ fontSize: '0.825rem' }}>등급 점수 : </span>
                            <span className="badge" style={{ fontSize: '0.825rem', backgroundColor: getBadgeStyleAndLabel(getRiskScore(freqBefore, sevBefore)).color, color: '#fff' }}>
                              {getBadgeStyleAndLabel(getRiskScore(freqBefore, sevBefore)).text}
                            </span>
                            {freqBefore && sevBefore && (
                              <span className="ms-2 fw-bold text-muted" style={{ fontSize: '0.825rem' }}>
                                ({getBadgeStyleAndLabel(getRiskScore(freqBefore, sevBefore)).label})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <h6 className="fw-bold text-secondary mb-3" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-shield-alt me-1 text-success"></i> 개선 후 위험도{isAfterRiskRequired && <span className="text-danger ms-1">*</span>}
                      </h6>
                      <div className="p-3 rounded-2" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <div className="row g-2">
                          <div className="col-6">
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.825rem' }}>빈도</label>
                            <select ref={freqAfterRef} className="form-select" value={freqAfter} onChange={e => setFreqAfter(e.target.value)} disabled={readOnly} style={{ fontSize: '0.825rem' }}>
                              {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                          <div className="col-6">
                            <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.825rem' }}>강도</label>
                            <select ref={sevAfterRef} className="form-select" value={sevAfter} onChange={e => setSevAfter(e.target.value)} disabled={readOnly} style={{ fontSize: '0.825rem' }}>
                              {STRENGTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                          <div className="col-12 mt-2">
                            <span className="fw-bold text-secondary" style={{ fontSize: '0.825rem' }}>등급 점수 : </span>
                            <span className="badge" style={{ fontSize: '0.825rem', backgroundColor: getBadgeStyleAndLabel(getRiskScore(freqAfter, sevAfter)).color, color: '#fff' }}>
                              {getBadgeStyleAndLabel(getRiskScore(freqAfter, sevAfter)).text}
                            </span>
                            {freqAfter && sevAfter && (
                              <span className="ms-2 fw-bold text-muted" style={{ fontSize: '0.825rem' }}>
                                ({getBadgeStyleAndLabel(getRiskScore(freqAfter, sevAfter)).label})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 4. 조치 담당자 지정 */}
                  <div className="col-12" ref={assigneesRef}>
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                        <div className="card-header bg-transparent fw-bold border-bottom-0 pb-0" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-user me-1 text-secondary"></i> 담당자 정보 <span className="text-danger">*</span>
                        </div>
                        <div className="card-body pb-3 pt-2">
                          {/* 검색 바 */}
                          <div className="form-schedule-search-bar mb-2 d-flex">
                            <div className="d-flex justify-content-between align-items-end" style={{ flex: '0 0 80%' }}>
                              <div className="form-schedule-search-left d-flex align-items-center gap-2">
                                <input
                                  type="text"
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  disabled={readOnly}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (searchTerm.trim()) handleSearch(searchTerm);
                                    }
                                  }}
                                  placeholder="이름 검색"
                                  className="form-control d-inline-block w-auto"
                                  style={{ minWidth: '200px' }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }}
                                  onClick={() => handleSearch(searchTerm)}
                                  disabled={readOnly}
                                >
                                  검색
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }}
                                  onClick={() => setOrgPopupOpen(true)}
                                  disabled={readOnly}
                                >
                                  조직도 선택
                                </button>
                              </div>
                              <div className="form-schedule-count fw-bold text-secondary" style={{ fontSize: '0.85rem' }}>
                                Count : {assignees.length}
                              </div>
                            </div>
                            <div style={{ flex: '1' }}></div>
                          </div>
                          {/* 담당자 리스트 */}
                          <div className="form-schedule-content d-flex mb-0 align-items-center">
                            <div
                              className="form-schedule-list-box border rounded p-2"
                              style={{
                                flex: '0 0 80%',
                                height: isChargeExpanded ? 'auto' : '115px',
                                overflowY: isChargeExpanded ? 'visible' : 'auto',
                                backgroundColor: '#fff'
                              }}
                            >
                              {assignees.length === 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6c757d', fontSize: '0.8rem' }}>
                                  담당자가 지정되지 않았습니다.
                                </div>
                              ) : (
                                assignees.map((c) => (
                                  <label key={c.emp_no} className="d-block mb-1 fw-normal" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={c.checked || false}
                                      onChange={() => toggleCheck(c.emp_no)}
                                      disabled={readOnly}
                                      className="form-check-input me-2"
                                    />
                                    {c.name}{c.position ? ` / ${c.position}` : ''}{c.department ? ` (${c.department})` : ''}
                                  </label>
                                ))
                              )}
                            </div>
                            <div className="form-schedule-delete-buttons d-flex flex-column justify-content-center align-items-start gap-2 ms-2" style={{ flex: '1' }}>
                              {assignees.length > 4 && (
                                <button
                                  type="button"
                                  className="btn btn-sm text-secondary p-0 mb-1"
                                  onClick={() => setIsChargeExpanded(!isChargeExpanded)}
                                >
                                  <i className={`fas ${isChargeExpanded ? 'fa-minus' : 'fa-plus'}`} /> 펼치기
                                </button>
                              )}
                              <button type="button" className="btn btn-outline-secondary bg-white btn-sm px-3 py-1" style={{ fontSize: '0.75rem', borderColor: '#d1d5db', width: '80px' }} onClick={deleteSelected} disabled={readOnly}>선택삭제</button>
                              <button type="button" className="btn btn-outline-secondary bg-white btn-sm px-3 py-1" style={{ fontSize: '0.75rem', borderColor: '#d1d5db', width: '80px' }} onClick={deleteAll} disabled={readOnly}>전체삭제</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 5. 조치 일정 및 현황 */}
                  <div className="col-12">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="d-flex align-items-center flex-wrap gap-4">
                        <div className="d-flex align-items-center gap-2">
                          <label className="fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            <i className="fas fa-clipboard-check me-1 text-secondary"></i>조치 상태
                          </label>
                          <select className="form-select" value={actionStatus} onChange={e => setActionStatus(e.target.value)} disabled={readOnly} style={{ borderColor: '#cbd5e1', fontSize: '0.825rem', height: '34px', width: '130px' }}>
                            <option value="pending">미완료 (대기)</option>
                            <option value="in_progress">진행중</option>
                            <option value="completed">조치 완료</option>
                          </select>
                        </div>
                        <div className="d-flex align-items-center gap-2" ref={dueDateRef}>
                          <label className="fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            <i className="fas fa-calendar-alt me-1 text-secondary"></i>조치예정일{isDueDateRequired && <span className="text-danger ms-1">*</span>}
                          </label>
                          <CommonDatePicker value={dueDate} onChange={setDueDate} disabled={readOnly} placeholder="기한 선택" />
                        </div>
                        <div className="d-flex align-items-center gap-2" ref={doneDateRef}>
                          <label className="fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            <i className="fas fa-calendar-check me-1 text-secondary"></i>조치완료일{isDoneDateRequired && <span className="text-danger ms-1">*</span>}
                          </label>
                          <CommonDatePicker value={doneDate} onChange={setDoneDate} disabled={readOnly} placeholder="완료일 선택" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 사진 탭 컨텐츠 */}
              {activeTab === 'PHOTO' && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                          <i className="fas fa-image me-1 text-secondary"></i> 개선 전 현장 사진
                        </label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger shadow-sm"
                          style={{ borderRadius: '6px', fontSize: '0.75rem', padding: '4px 12px', fontWeight: 'bold', visibility: (!readOnly && beforePhoto) ? 'visible' : 'hidden' }}
                          onClick={() => !readOnly && beforePhoto && handlePhotoDeleteLocal(beforePhoto, 'BEFORE')}
                        >
                          <i className="fas fa-trash-alt me-1"></i> 삭제
                        </button>
                      </div>
                      <div className="card-body text-center p-0">
                        {beforePhoto ? (
                          <div className="w-100 rounded-3 overflow-hidden border bg-light d-flex align-items-center justify-content-center" style={{ borderColor: '#e2e8f0', height: '402px' }}>
                            <img
                              src={beforePhoto.url}
                              alt="개선 전"
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'pointer' }}
                              onClick={() => setLightboxUrl(beforePhoto.url)}
                              onError={() => {
                                setPhotos(prev => prev.filter(p => p.type !== 'BEFORE'));
                                if (pendingBeforeFile) setPendingBeforeFile(null);
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="w-100 d-flex flex-column align-items-center justify-content-center"
                            style={{ height: '402px', border: readOnly ? '1px solid #cbd5e1' : '1px dashed #cbd5e1', borderRadius: '12px', backgroundColor: '#f8fafc', cursor: readOnly ? 'default' : 'pointer', transition: 'all 0.2s' }}
                            onClick={() => !readOnly && document.getElementById('beforePhotoInput').click()}
                            onDragOver={(e) => { if (readOnly) return; e.preventDefault(); e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                            onDragLeave={(e) => { if (readOnly) return; e.preventDefault(); e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                            onDrop={(e) => { if (readOnly) return; e.preventDefault(); e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; if (e.dataTransfer.files.length) handlePhotoUploadLocal('BEFORE', { target: { files: e.dataTransfer.files } }); }}
                          >
                            {!readOnly ? (
                              <>
                                <i className="fas fa-cloud-upload-alt text-primary opacity-75 mb-3" style={{ fontSize: '2.5rem' }}></i>
                                <input id="beforePhotoInput" type="file" className="d-none" onChange={e => handlePhotoUploadLocal('BEFORE', e)} accept="image/*" />
                                <p className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>클릭하거나 이미지를 드래그하세요</p>
                                <p className="small text-muted mb-0" style={{ fontSize: '0.75rem' }}>지원 형식: JPG, PNG 이미지 파일</p>
                              </>
                            ) : (
                              <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>등록된 사진 없음</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                          <i className="fas fa-image me-1 text-success"></i> 개선 후 현장 사진
                        </label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger shadow-sm"
                          style={{ borderRadius: '6px', fontSize: '0.75rem', padding: '4px 12px', fontWeight: 'bold', visibility: (!readOnly && afterPhoto) ? 'visible' : 'hidden' }}
                          onClick={() => !readOnly && afterPhoto && handlePhotoDeleteLocal(afterPhoto, 'AFTER')}
                        >
                          <i className="fas fa-trash-alt me-1"></i> 삭제
                        </button>
                      </div>
                      <div className="card-body text-center p-0">
                        {afterPhoto ? (
                          <div className="w-100 rounded-3 overflow-hidden border bg-light d-flex align-items-center justify-content-center" style={{ borderColor: '#e2e8f0', height: '402px' }}>
                            <img
                              src={afterPhoto.url}
                              alt="개선 후"
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'pointer' }}
                              onClick={() => setLightboxUrl(afterPhoto.url)}
                              onError={() => {
                                setPhotos(prev => prev.filter(p => p.type !== 'AFTER'));
                                if (pendingAfterFile) setPendingAfterFile(null);
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="w-100 d-flex flex-column align-items-center justify-content-center"
                            style={{ height: '402px', border: readOnly ? '1px solid #cbd5e1' : '1px dashed #cbd5e1', borderRadius: '12px', backgroundColor: '#f8fafc', cursor: readOnly ? 'default' : 'pointer', transition: 'all 0.2s' }}
                            onClick={() => !readOnly && document.getElementById('afterPhotoInput').click()}
                            onDragOver={(e) => { if (readOnly) return; e.preventDefault(); e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                            onDragLeave={(e) => { if (readOnly) return; e.preventDefault(); e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                            onDrop={(e) => { if (readOnly) return; e.preventDefault(); e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; if (e.dataTransfer.files.length) handlePhotoUploadLocal('AFTER', { target: { files: e.dataTransfer.files } }); }}
                          >
                            {!readOnly ? (
                              <>
                                <i className="fas fa-cloud-upload-alt text-primary opacity-75 mb-3" style={{ fontSize: '2.5rem' }}></i>
                                <input id="afterPhotoInput" type="file" className="d-none" onChange={e => handlePhotoUploadLocal('AFTER', e)} accept="image/*" />
                                <p className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>클릭하거나 이미지를 드래그하세요</p>
                                <p className="small text-muted mb-0" style={{ fontSize: '0.75rem' }}>지원 형식: JPG, PNG 이미지 파일</p>
                              </>
                            ) : (
                              <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>등록된 사진 없음</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ borderTop: '1px solid #cbd5e1', padding: '10px 24px', display: 'flex', gap: '3px', justifyContent: 'flex-end' }}>
              {!readOnly && <button type="submit" className="btn btn-primary" style={{ fontWeight: 'bold' }}>저장</button>}
              <button type="button" className="btn btn-secondary" style={{ fontWeight: 'bold' }} onClick={onClose}>닫기</button>
            </div>
          </form>
        </div>
      </div>

      {/* 검색 결과 모달 */}
      {showSearchPopup && (
        <ModalSchedule
          type="searchResult"
          data={{ users: searchUsers, depts: [] }}
          toggleSelect={toggleSearchSelect}
          onConfirm={handleSearchConfirm}
          onClose={() => { setShowSearchPopup(false); setSearchUsers([]); }}
        />
      )}

      {/* 조직도 팝업 */}
      {orgPopupOpen && (
        <OrgTreeSelect
          selectionMode="multi"
          targetType="user"
          initialSelected={assignees.map(w => ({
            target_type: 'user',
            target_id: w.emp_no,
            target_name: w.name,
            position: w.position,
            dept_name: w.department,
            disp_target_name: `${w.name}${w.position ? " / " + w.position : ""}${w.department ? " (" + w.department + ")" : ""}`
          }))}
          onConfirm={handleOrgConfirm}
          onCancel={() => setOrgPopupOpen(false)}
        />
      )}
      {/* 이미지 라이트박스 */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <button
            style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer' }}
            onClick={() => setLightboxUrl(null)}
          >
            <i className="fas fa-times"></i>
          </button>
          <img
            src={lightboxUrl}
            alt="원본 보기"
            style={{ maxWidth: '90%', maxHeight: '85vh', objectFit: 'contain', borderRadius: '4px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }}
          />
        </div>
      )}
    </div>,
    document.body
  );
}

export default RiskFormModal;
